import type {AvmSpecOutputOnly, AvmSpecParamCategory} from "../reva-spec";

/** Ein Eintrag aus `micro_json`: markiert eine Textstelle als Keyword, mit ihrer aktuellen Kategorie. */
export interface DescriptionKeyword {
    key: number;
    category_number?: number | null;
    offset?: number | null;
    length?: number | null;
}

/** Response des `locationInformation/description`-Endpoints (AVM). */
export interface LocationInfoDescriptionData {
    macro_text: string | null;
    micro_text: string | null;
    market_text?: string | null; // #19165: Wohnungsmarktbeschreibung; fehlt/leer bei alter AVM-DB (bis Q2.5-Release)
    micro_json?: DescriptionKeyword[] | null;
    // meso_text folgt später
}

/** Ausprägungen je micro_json-Key, indiziert mit dessen numerischer category_number (z.B. 600 für "oepnv"). */
export type DescriptionKeywordCategoriesMap = Record<number, AvmSpecParamCategory[]>;
/** Klartext-Name (shortNameEn) je numerischem micro_json-Key*/
export type DescriptionKeywordKeyNames = Record<number, string>;

export interface DescriptionKeywordData {
    categories: DescriptionKeywordCategoriesMap;
    keyNames: DescriptionKeywordKeyNames;
}

const categoryBucket = (categoryNumber: number) => Math.floor(categoryNumber / 100);

/**
 * Der Server nummeriert Ausprägungen im selben Hunderter-Block wie ihren Key (zentralitaet=100 →
 * Ausprägungen 101-103, oepnv=600 → Ausprägungen 601-605, ...) — daraus wird die Zuordnung erzeugt
 */
export function deriveDescriptionKeywordData(
    descriptionSpec: AvmSpecOutputOnly | null | undefined,
): DescriptionKeywordData {
    const microJsonModel = descriptionSpec?.outputParameters.find((param) => param.parameter === "micro_json")?.model;
    const keyCategories = microJsonModel?.properties.find((property) => property.property === "key")?.categories ?? [];
    const valueCategories =
        microJsonModel?.properties.find((property) => property.property === "category_number")?.categories ?? [];

    return {
        categories: Object.fromEntries(
            keyCategories.map((keyCategory) => [
                keyCategory.categoryNumber,
                valueCategories.filter(
                    (value) => categoryBucket(value.categoryNumber) === categoryBucket(keyCategory.categoryNumber),
                ),
            ]),
        ),
        keyNames: Object.fromEntries(
            keyCategories.map((keyCategory) => [keyCategory.categoryNumber, keyCategory.shortNameEn]),
        ),
    };
}

export interface KeywordToken {
    key: number;
    categoryNumber: number;
    matchStart: number;
    matchEnd: number;
}

const KEYWORD_TOKEN_PATTERN = /\[(\d+):(\d+)]/g;

/** Findet jedes wohlgeformte `[key:category_number]`-Token in `templatedText`, in Textreihenfolge. */
export function parseKeywordTokens(templatedText: string): KeywordToken[] {
    return [...templatedText.matchAll(KEYWORD_TOKEN_PATTERN)].map((match) => ({
        key: Number(match[1]),
        categoryNumber: Number(match[2]),
        matchStart: match.index,
        matchEnd: match.index + match[0].length,
    }));
}

/** Gegenstück zu tokenizeText (client-seitig): ersetzt jedes `[key:category_number]`-Token durch das
 * deutsche Klartext-Wort seiner Kategorie (immer Deutsch, kein i18n hier — Client und PDF sind beide
 * immer deutsch für diesen Text). Ein Token mit unbekanntem Key/unbekannter category_number bleibt
 * unverändert im Text stehen (z.B. weil im Freitext manuell verändert). */
export function detokenizeText(
    templatedText: string,
    descriptionKeywordCategories: DescriptionKeywordCategoriesMap,
): string {
    let plainText = "";
    let cursor = 0;
    for (const token of parseKeywordTokens(templatedText)) {
        const category = (descriptionKeywordCategories[token.key] ?? []).find(
            (c) => c.categoryNumber === token.categoryNumber,
        );
        const word = category ? category.shortNameDe : templatedText.slice(token.matchStart, token.matchEnd);
        plainText += templatedText.slice(cursor, token.matchStart) + word;
        cursor = token.matchEnd;
    }
    return plainText + templatedText.slice(cursor);
}
